import React from 'react';
import { Checkbox } from 'primereact/checkbox';
import {
  getFieldDataTypeError,
  type DataManagementFieldConfig
} from './dataManagementFormConfig';
import './DataManagementForm.scss';
import { TipTapEditor } from '../Common/TipTapEditor';

export interface DataManagementOption {
  label: string;
  value: string | number;
  id?: string | number | null;
}

interface DataManagementFormProps {
  fields: readonly DataManagementFieldConfig[];
  values: Record<string, any> | null;
  onChange: (values: Record<string, any>) => void;
  options?: Record<string, DataManagementOption[]>;
  isEditing?: boolean;
}

const DataManagementForm: React.FC<DataManagementFormProps> = ({
  fields,
  values,
  onChange,
  options = {},
  isEditing = false
}) => {
  const formValues = values || {};

  const updateValue = (
    field: DataManagementFieldConfig,
    value: unknown
  ) => {
    if (
      isEditing &&
      field.disabled_on_edit
    ) {
      return;
    }

    const nextValues = {
      ...formValues,
      [field.field_name]: value
    };

    if (field.linked_id_field) {
      const selectedOption =
        options[field.field_name]?.find(
          option =>
            String(option.value) ===
            String(value)
        );

      nextValues[field.linked_id_field] =
        selectedOption?.id ?? null;
    }

    if (
      formValues[field.field_name] !==
      value
    ) {
      const resetDependents = (
        parentFieldName: string
      ) => {
        fields
          .filter(
            dependentField =>
              dependentField.depends_on ===
              parentFieldName
          )
          .forEach(
            dependentField => {
              nextValues[
                dependentField.field_name
              ] =
                dependentField.field_type ===
                'checkbox'
                  ? false
                  : '';

              if (
                dependentField.linked_id_field
              ) {
                nextValues[
                  dependentField.linked_id_field
                ] = null;
              }

              resetDependents(
                dependentField.field_name
              );
            }
          );
      };

      resetDependents(
        field.field_name
      );
    }

    onChange(nextValues);
  };

  return (
    <div className="dm-form">
      {fields.map(field => {
        const fieldId =
          `dm-field-${field.field_name}`;

        const value =
          formValues[field.field_name];

        const disabled =
          (
            !!field.depends_on &&
            !formValues[
              field.depends_on
            ]
          ) ||
          (
            isEditing &&
            !!field.disabled_on_edit
          );

        const typeError =
          getFieldDataTypeError(
            field,
            value
          );

        const label = (
          <span className="dm-form-label">
            {field.label}

            {field.is_required && (
              <span
                className="dm-required"
                aria-label="required"
              >
                *
              </span>
            )}
          </span>
        );

        if (
          field.field_type ===
          'checkbox'
        ) {
          return (
            <div
              className="dm-checkbox-field dm-form-field--checkbox"
              key={field.field_name}
            >
              <Checkbox
                inputId={fieldId}
                checked={!!value}
                disabled={disabled}
                onChange={event =>
                  updateValue(
                    field,
                    !!event.checked
                  )
                }
              />

              <label htmlFor={fieldId}>
                {label}
              </label>
            </div>
          );
        }

        return (
          <label
            className={`dm-form-field dm-form-field--${field.field_type}`}
            htmlFor={fieldId}
            key={field.field_name}
          >
            {label}

            {field.field_type ===
              'select' && (
              <select
                id={fieldId}
                value={value ?? ''}
                disabled={disabled}
                required={
                  field.is_required
                }
                onChange={event =>
                  updateValue(
                    field,
                    event.target.value
                  )
                }
              >
                <option value="">
                  {field.placeholder ||
                    `Select ${field.label}`}
                </option>

                {(
                  options[
                    field.field_name
                  ] || []
                ).map(
                  (
                    option,
                    index
                  ) => (
                    <option
                      key={`${option.value}-${index}`}
                      value={
                        option.value
                      }
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
            )}

            {(
              field.field_type ===
                'text' ||
              field.field_type ===
                'number'
            ) && (
              <input
                id={fieldId}
                type={
                  field.field_type
                }
                value={value ?? ''}
                required={
                  field.is_required
                }
                step={field.step}
                disabled={disabled}
                aria-invalid={
                  !!typeError
                }
                min={0}
                onChange={event =>
                  updateValue(
                    field,
                    event.target.value
                  )
                }
              />
            )}

            {field.field_type ===
              'textarea' && (
              <textarea
                id={fieldId}
                value={value ?? ''}
                required={
                  field.is_required
                }
                rows={
                  field.rows || 3
                }
                disabled={disabled}
                aria-invalid={
                  !!typeError
                }
                onChange={event =>
                  updateValue(
                    field,
                    event.target.value
                  )
                }
              />
            )}

            {field.field_type ===
              'json' && (
              <textarea
                id={fieldId}
                className="dm-form-field-json"
                value={value ?? ''}
                required={
                  field.is_required
                }
                rows={
                  field.rows || 4
                }
                placeholder={
                  field.placeholder
                }
                disabled={disabled}
                aria-invalid={
                  !!typeError
                }
                onChange={event =>
                  updateValue(
                    field,
                    event.target.value
                  )
                }
              />
            )}

            {field.field_type ===
              'editor' && (
              <TipTapEditor
                id={fieldId}
                value={value ?? ''}
                onTextChange={event =>
                  updateValue(
                    field,
                    event.htmlValue ||
                      ''
                  )
                }
                style={{
                  height: '150px'
                }}
                readOnly={disabled}
              />
            )}

            {typeError && (
              <span className="dm-field-error">
                {typeError}
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
};

export default DataManagementForm;